import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectClient } from '@/model';
import {
  BenefitDesignQuoteCard,
  TraditionalLTCQuoteCard,
  NoQuotesAvailable,
  QuoteErrorDisplay,
  type BenefitDesignQuoteGroup,
  type TraditionalLTCQuoteWithROI,
  type PaymentOption,
} from '@/features/shop-insurance';
import { VALIDATION_ERROR_TYPES } from '@client/util/quoteTypeGuards';

interface BenefitDesignQuotesListProps {
  quoteGroups: BenefitDesignQuoteGroup[];
  filteredTraditionalLTCQuotesWithEligibility: TraditionalLTCQuoteWithROI[];
  underwritingResults: Map<string, 'eligible' | 'ineligible' | 'unsure' | null>;
  selectedTab: ShopInsuranceTab;
  clientPartner: any;
  paymentOption: PaymentOption;
  advisorId: string;
  isQuoteSelected: (quote: any) => { isSelected: boolean; fundingSourceId: string | null };
  handleViewUnderwritingDetails: (quoteId: string, productName: string) => void;
  onClose: () => void;
  knownLimitationError?: QuoteError | null;
}

export function BenefitDesignQuotesList({
  quoteGroups,
  filteredTraditionalLTCQuotesWithEligibility,
  underwritingResults,
  selectedTab,
  clientPartner,
  paymentOption,
  advisorId,
  isQuoteSelected,
  handleViewUnderwritingDetails,
  onClose,
  knownLimitationError,
}: BenefitDesignQuotesListProps) {
  const client = useSelector(selectClient);
  const ltcAtAge = client?.appliedInferenceSet?.ltcAtAge;
  const clientAge = client?.intakeSurvey?.clientAge;
  const yearsUntilLtc = ltcAtAge && clientAge ? ltcAtAge - clientAge : 0;

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  function toggleId(id: string) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  if (knownLimitationError && VALIDATION_ERROR_TYPES.includes(knownLimitationError.type)) {
    return <QuoteErrorDisplay error={knownLimitationError} />;
  }

  const hasAnyQuotes =
    quoteGroups.length > 0 || filteredTraditionalLTCQuotesWithEligibility.length > 0;

  if (!hasAnyQuotes) {
    return <NoQuotesAvailable advisorId={advisorId} reason={knownLimitationError?.type} />;
  }

  return (
    <div
      className="flex w-full flex-col gap-4"
      data-testid="benefit-design-quotes-list"
    >
      {quoteGroups.map(group => {
        const id = group.representativeQuote.insuranceQuoteId;
        const underwritingResult = underwritingResults.get(id);

        return (
          <BenefitDesignQuoteCard
            key={id}
            testId="benefit-design-quote-card"
            group={group}
            onClose={onClose}
            selectedTab={selectedTab}
            clientPartner={clientPartner}
            isQuoteSelected={isQuoteSelected}
            underwritingResult={underwritingResult}
            onViewUnderwritingDetails={() => handleViewUnderwritingDetails(id, group.productName)}
            ltcAtAge={ltcAtAge}
            yearsUntilLtc={yearsUntilLtc}
          />
        );
      })}

      {filteredTraditionalLTCQuotesWithEligibility.map(quote => {
        const id = quote.traditionalLTCQuoteId;
        const { isSelected, fundingSourceId } = isQuoteSelected(quote);
        const underwritingResult = underwritingResults.get(id);

        return (
          <TraditionalLTCQuoteCard
            key={id}
            testId="benefit-design-traditional-quote-card"
            quote={quote}
            isExpanded={expandedIds.has(id)}
            onToggleExpand={() => toggleId(id)}
            onClose={onClose}
            isSelected={isSelected}
            fundingSourceId={fundingSourceId}
            underwritingResult={underwritingResult}
            onViewUnderwritingDetails={() => handleViewUnderwritingDetails(id, quote.productName)}
          />
        );
      })}
    </div>
  );
}
